/**
 * Functions exported from this file return user–facing URLs on which
 * fock application is expected to be mounted on.
 * a.k.a. “external routes” or “mount points”
 *
 * Do not confuse with server/utils/routes.
 */

import { format as formatUrl, parse as parseUrl } from 'url'

import { stringify } from 'query-string'
import { applyRouteParams, createTenantPath } from 'server/utils/routing'
import { normalizeUrl } from 'shared/utils/url'
import { Experiment } from 'server/shop-config/types'
import { isExperimentVariableTrue } from 'shared/experiments/utils'
import { ExperimentName } from 'shared/experiments/consts'

export type QueryParamsObject = Record<string, unknown>

abstract class MountPointsBase {
  protected readonly mountPoints: Record<string, unknown>
  protected readonly tenant: string
  protected readonly language: string
  protected readonly coreShopEnvironment: string
  protected readonly experiments: Experiment[]

  constructor({
    mountPoints,
    tenant,
    language,
    coreShopEnvironment,
    experiments = []
  }) {
    this.mountPoints = mountPoints
    this.tenant = tenant
    this.language = language
    this.coreShopEnvironment = coreShopEnvironment
    this.experiments = experiments
  }

  // eslint-disable-next-line class-methods-use-this
  setQueryParams(url: string, additionalQuery: QueryParamsObject): string {
    const { query, ...parsedUrl } = parseUrl(url, true)
    return formatUrl({
      ...parsedUrl,
      search: stringify({ ...query, ...additionalQuery })
    })
  }

  applyCommonRouteParams(route: string): string {
    const tenantPath = createTenantPath(this.tenant, this.coreShopEnvironment)
    return applyRouteParams(route, {
      tenantPath,
      language: this.language
    })
  }

  getUrl(key): string {
    const mountPoint = this.mountPoints[key]

    if (!mountPoint) {
      throw new Error(`mount point for key ${key} does not exist`)
    }

    return normalizeUrl(`${mountPoint}`)
  }

  isExperimentVariableEnabled(key: ExperimentName, variable: string): boolean {
    return isExperimentVariableTrue(key, variable, this.experiments)
  }
}

export default MountPointsBase
